<template>
  <div class="page boss-page strategy-map-page">
    <van-nav-bar title="战略行动地图" left-arrow fixed placeholder @click-left="$router.back()"/>

    <section class="actions">
      <button v-if="isBoss" class="workbench-entry" @click="goMaintain">
        <van-icon name="plus"/>
        <span><b>维护战略与 OKR</b><small>发布公司战略、事业部 OKR 与承接关系</small></span>
        <em>进入</em>
      </button>
      <button v-else class="workbench-entry" @click="goWorkbench">
        <van-icon name="cluster-o"/>
        <span><b>战略执行工作台</b><small>复审任务、分配人员并查看团队执行</small></span>
        <em>进入</em>
      </button>
    </section>

    <section class="stats">
      <div><b>{{ nodes.length }}</b><span>战略节点</span></div>
      <div><b>{{ links.length }}</b><span>层级关系</span></div>
      <div><b>{{ bindings.length }}</b><span>执行绑定</span></div>
      <div><b>{{ gaps.length }}</b><span>待补断点</span></div>
    </section>

    <section v-if="gaps.length" class="panel gaps">
      <h2>承接断点</h2>
      <p v-for="g in gaps" :key="g.nodeId">{{ g.message }}</p>
    </section>

    <section class="panel chain-panel">
      <h2>公司战略承接链</h2>
      <p class="chain">公司战略 → 事业部战略 → 事业部目标（OKR）→ 项目 → 任务 → 人员 → 工作要点</p>
      <div v-for="type in types" :key="type.code" class="layer">
        <header>
          <span>{{ type.name }}</span>
          <em>{{ byType(type.code).length }}</em>
        </header>
        <button v-for="n in byType(type.code)" :key="field(n,'id')" class="node-card" @click="openNodeDetail(n)">
          <span class="node-main">
            <b>{{ field(n, 'name') }}</b>
            <small>{{ field(n, 'description') || '暂无说明' }}</small>
            <small v-if="isOkrType(type.code)" class="progress">进度 {{ okrProgress(field(n, 'id')) }}%</small>
          </span>
          <van-icon name="arrow"/>
        </button>
        <p v-if="!byType(type.code).length" class="empty">尚未配置</p>
      </div>
    </section>

    <van-popup v-model="detailVisible" position="bottom" round get-container="body" class="detail-popup">
      <div v-if="selectedNode" class="detail-sheet">
        <header>
          <span class="detail-tag">{{ nodeTypeLabel(field(selectedNode, 'node_type')) }}</span>
          <h3>{{ field(selectedNode, 'name') }}</h3>
          <small v-if="nodeStatus(field(selectedNode, 'status'))">{{ nodeStatus(field(selectedNode, 'status')) }}</small>
        </header>
        <div class="detail-body">
          <p class="detail-desc">{{ field(selectedNode, 'description') || '暂无方向说明' }}</p>
          <div v-if="field(selectedNode, 'business_unit_sysno') || field(selectedNode, 'business_unit_name')" class="detail-row">
            <label>事业部</label><span>{{ field(selectedNode, 'business_unit_name') || field(selectedNode, 'business_unit_sysno') }}</span>
          </div>
          <template v-if="selectedOkr">
            <div class="detail-row"><label>OKR 目标</label><span>{{ field(selectedOkr, 'objective') || '—' }}</span></div>
            <div class="detail-row"><label>进度</label><span>{{ okrProgress(field(selectedNode, 'id')) }}%</span></div>
            <div v-if="okrKeyResults.length" class="detail-kr">
              <label>关键结果</label>
              <ul><li v-for="(kr, i) in okrKeyResults" :key="i">{{ kr }}</li></ul>
            </div>
          </template>
          <div v-if="detailParents.length" class="detail-links">
            <label>上级承接</label>
            <button v-for="p in detailParents" :key="field(p,'id')" class="link-chip" @click="openNodeDetail(p)">
              {{ field(p, 'name') }}
            </button>
          </div>
          <div v-if="detailChildren.length" class="detail-links">
            <label>下游承接</label>
            <button v-for="c in detailChildren" :key="field(c,'id')" class="link-chip" @click="openNodeDetail(c)">
              {{ field(c, 'name') }}
            </button>
          </div>
        </div>
        <van-button block type="danger" class="detail-close" @click="detailVisible=false">关闭</van-button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import api from '@/utils/coreapi';

export default {
  data() {
    return {
      nodes: [],
      links: [],
      bindings: [],
      gaps: [],
      okrs: [],
      detailVisible: false,
      selectedNode: null,
      types: [
        {code: 'COMPANY_STRATEGY', legacy: 'STRATEGY', name: '公司战略'},
        {code: 'BUSINESS_UNIT_STRATEGY', name: '事业部战略'},
        {code: 'BUSINESS_UNIT_OKR', legacy: 'DEPARTMENT_GOAL', name: '事业部目标（OKR）'},
        {code: 'PROJECT', name: '项目'},
        {code: 'TASK', name: '任务'},
        {code: 'ACTION', name: '人员与工作要点'},
        {code: 'OUTCOME', name: '成果'}
      ]
    };
  },
  computed: {
    isBoss() {
      return String(localStorage.getItem('positionFamily') || localStorage.getItem('PositionFamily') || '').toUpperCase() === 'GENERAL_MANAGER';
    },
    selectedOkr() {
      if (!this.selectedNode) return null;
      return this.okrs.find(v => Number(this.field(v, 'strategy_node_id')) === Number(this.field(this.selectedNode, 'id')));
    },
    okrKeyResults() {
      if (!this.selectedOkr) return [];
      const raw = this.field(this.selectedOkr, 'key_results_json');
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (e) {
        return String(raw).split('\n').filter(Boolean);
      }
    },
    detailParents() {
      if (!this.selectedNode) return [];
      return this.relatedNodes('parent');
    },
    detailChildren() {
      if (!this.selectedNode) return [];
      return this.relatedNodes('child');
    }
  },
  created() {
    this.load();
  },
  methods: {
    field(r, k) {
      if (!r) return '';
      return r[k] !== undefined ? r[k] : r[k.toUpperCase()];
    },
    byType(t) {
      return this.nodes.filter(n => {
        const type = this.field(n, 'node_type');
        const def = this.types.find(v => v.code === t);
        return type === t || (def && def.legacy === type);
      });
    },
    isOkrType(code) {
      return code === 'BUSINESS_UNIT_OKR';
    },
    nodeTypeLabel(type) {
      const map = {
        COMPANY_STRATEGY: '公司战略',
        BUSINESS_UNIT_STRATEGY: '事业部战略',
        BUSINESS_UNIT_OKR: '事业部 OKR',
        STRATEGY: '公司战略',
        DEPARTMENT_GOAL: '事业部 OKR',
        PROJECT: '项目',
        TASK: '任务',
        ACTION: '工作要点',
        OUTCOME: '成果'
      };
      return map[String(type || '').toUpperCase()] || type;
    },
    nodeStatus(status) {
      const s = String(status || '').toUpperCase();
      return {DRAFT: '草稿', ACTIVE: '已发布', INACTIVE: '已停用'}[s] || '';
    },
    okrProgress(id) {
      const row = this.okrs.find(v => Number(this.field(v, 'strategy_node_id')) === Number(id));
      return row ? Number(this.field(row, 'progress_percent') || 0).toFixed(0) : 0;
    },
    relatedNodes(direction) {
      const id = Number(this.field(this.selectedNode, 'id'));
      const isParent = direction === 'parent';
      return this.links
        .filter(l => Number(this.field(l, isParent ? 'child_node_id' : 'parent_node_id')) === id)
        .map(l => {
          const targetId = Number(this.field(l, isParent ? 'parent_node_id' : 'child_node_id'));
          return this.nodes.find(n => Number(this.field(n, 'id')) === targetId);
        })
        .filter(Boolean);
    },
    openNodeDetail(n) {
      this.selectedNode = n;
      this.detailVisible = true;
    },
    goMaintain() {
      this.$router.push({name: 'BossStrategyExecution'});
    },
    goWorkbench() {
      this.$router.push({name: 'SupervisorStrategyWorkbench'});
    },
    async load() {
      try {
        const r = await api.strategyActionMap();
        this.nodes = r.nodes || [];
        this.links = r.links || [];
        this.bindings = r.executionBindings || [];
        this.okrs = r.okrDefinitions || [];
        this.gaps = r.gaps || [];
      } catch (e) {
        this.$toast.fail(e.message);
      }
    }
  }
};
</script>

<style lang="less" scoped>
@import '../../boss/boss.less';
@import './supervisor.less';

.strategy-map-page {
  min-height: 100vh;
  margin: 0;
  padding-bottom: 1.2rem;
  background: #f6f7fb;
  color: #34364a;
}

.strategy-map-page > .van-nav-bar.van-nav-bar--fixed,
.strategy-map-page > .van-nav-bar,
.strategy-map-page /deep/ .van-nav-bar {
  top: 0 !important;
  left: 0 !important;
  margin: 0 !important;
  width: 100vw !important;
  max-width: 100% !important;
  border-radius: 0 !important;
}

.actions {
  margin: .2rem .28rem 0;
}

.workbench-entry {
  width: 100%;
  min-height: 1.28rem;
  padding: .24rem .22rem .24rem .24rem;
  display: flex;
  align-items: center;
  gap: .16rem;
  text-align: left;
  border: 0;
  border-radius: .18rem;
  background: #fff;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
  cursor: pointer;
}

.workbench-entry:active {
  background: #fafafa;
}

.workbench-entry > .van-icon:first-child {
  width: .72rem;
  height: .72rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 .72rem;
  color: #c62828;
  font-size: .38rem;
  border-radius: .14rem;
  background: #fff5f5;
}

.workbench-entry span {
  min-width: 0;
  flex: 1;
}

.workbench-entry b,
.workbench-entry small {
  display: block;
}

.workbench-entry b {
  font-size: .3rem;
  color: #34364a;
  font-weight: 600;
}

.workbench-entry small {
  margin-top: .06rem;
  color: #8b8e9d;
  font-size: .22rem;
  line-height: 1.45;
}

.workbench-entry em {
  flex: 0 0 auto;
  min-width: .88rem;
  padding: .1rem .24rem;
  color: #fff;
  text-align: center;
  border-radius: .28rem;
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
  font-size: .24rem;
  font-style: normal;
  font-weight: 500;
  box-shadow: 0 .04rem .12rem rgba(198, 40, 40, .25);
}

.stats, .panel {
  margin: .2rem .28rem 0;
  background: #fff;
  border-radius: .18rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

.stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}

.stats div {
  padding: .24rem .08rem;
  text-align: center;
}

.stats b, .stats span {
  display: block;
}

.stats b {
  color: #c62828;
  font-size: .38rem;
}

.stats span {
  font-size: .22rem;
  color: #888;
}

.panel {
  padding: .28rem .26rem;
}

h2 {
  margin: 0 0 .18rem;
  font-size: .34rem;
  color: #30313a;
}

.chain {
  padding: .18rem .2rem;
  background: #fff5f5;
  border-radius: .1rem;
  color: #8f2626;
  font-size: .24rem;
  line-height: 1.6;
}

.gaps {
  border-left: .06rem solid #e39a29;
}

.gaps p {
  margin: .08rem 0 0;
  color: #8b642b;
  font-size: .26rem;
}

.layer {
  padding: .18rem 0 0;
  border-top: 1PX solid #eee;
}

.layer:first-of-type {
  border-top: 0;
  padding-top: .08rem;
}

.layer header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: .1rem;
  color: #c62828;
  font-size: .26rem;
  font-weight: 600;
}

.layer header em {
  min-width: .36rem;
  padding: 0 .1rem;
  color: #fff;
  text-align: center;
  border-radius: .2rem;
  background: #d32f2f;
  font-size: .2rem;
  font-style: normal;
}

.node-card {
  width: 100%;
  margin-top: .12rem;
  padding: .18rem .16rem .18rem .2rem;
  display: flex;
  align-items: center;
  gap: .12rem;
  text-align: left;
  border: 0;
  border-radius: .12rem;
  background: #f8f8fa;
  cursor: pointer;
}

.node-card:active {
  background: #f0f0f4;
}

.node-main {
  min-width: 0;
  flex: 1;
}

.node-main b, .node-main small {
  display: block;
}

.node-main b {
  font-size: .28rem;
  color: #34364a;
}

.node-main small {
  margin-top: .06rem;
  color: #858899;
  font-size: .23rem;
  line-height: 1.45;
}

.node-main .progress {
  color: #c62828;
}

.node-card .van-icon {
  color: #b0b2c0;
  font-size: .28rem;
}

.empty {
  margin: .1rem 0 0;
  color: #999;
  font-size: .24rem;
}

.detail-popup {
  max-height: 82vh;
}

.detail-sheet {
  padding: .32rem .28rem .4rem;
}

.detail-sheet header {
  margin-bottom: .2rem;
}

.detail-tag {
  display: inline-block;
  padding: .04rem .12rem;
  color: #c62828;
  font-size: .22rem;
  border-radius: .08rem;
  background: #fff0f0;
}

.detail-sheet h3 {
  margin: .12rem 0 .06rem;
  font-size: .36rem;
  color: #30313a;
}

.detail-sheet header > small {
  color: #8b8e9d;
  font-size: .22rem;
}

.detail-desc {
  margin: 0 0 .2rem;
  color: #555;
  font-size: .26rem;
  line-height: 1.6;
}

.detail-row {
  display: flex;
  gap: .16rem;
  margin-bottom: .14rem;
  font-size: .25rem;
}

.detail-row label {
  flex: 0 0 1.4rem;
  color: #8b8e9d;
}

.detail-row span {
  flex: 1;
  color: #34364a;
}

.detail-kr label, .detail-links label {
  display: block;
  margin-bottom: .08rem;
  color: #8b8e9d;
  font-size: .24rem;
}

.detail-kr ul {
  margin: 0 0 .16rem;
  padding-left: 1.1em;
  color: #34364a;
  font-size: .25rem;
}

.detail-links {
  margin-top: .12rem;
}

.link-chip {
  display: inline-block;
  margin: 0 .1rem .1rem 0;
  padding: .08rem .16rem;
  color: #c62828;
  font-size: .23rem;
  border: 1PX solid #f0c8c8;
  border-radius: .08rem;
  background: #fff;
}

.detail-close {
  margin-top: .24rem;
  border-radius: .12rem;
}
</style>



// WEBPACK FOOTER //
// src/views/efficiency/supervisor/StrategyActionMap.vue